import React, { useEffect, useRef, useState } from 'react';

const CASES = [
  { who: 'CEO, Series C SaaS', decision: 'Cut 18% of headcount now, or raise a bridge at a down round?' },
  { who: 'CFO, Mid-Cap Retailer', decision: 'Lock in tariffs today, or wait on the trade ruling next quarter?' },
  { who: 'Central Bank Governor', decision: 'Hold rates through the cycle, or cut ahead of the inflation print?' },
  { who: 'Chief Medical Officer', decision: 'Fast-track under emergency authorization, or wait for Phase III data?' },
  { who: 'Energy Minister', decision: 'Approve the new gas terminal, or hold the 2030 emissions target?' },
  { who: 'Head of AI Safety', decision: 'Ship with current safeguards, or delay for a second red-team pass?' },
  { who: 'University Provost', decision: 'Adopt the AI admissions screen, or keep the process fully human?' },
];

const AUTOPLAY_MS = 4800;

export default function IvoriDecisionBoard({ sectionRef }) {
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const [active, setActive] = useState(0);
  const autoplayRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const cardStep = () => {
    const track = trackRef.current;
    const card = cardRefs.current[0];
    if (!track || !card) return 0;
    const style = window.getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || '22');
    return card.offsetWidth + gap;
  };

  const scrollToCard = (i) => {
    const clamped = Math.max(0, Math.min(CASES.length - 1, i));
    setActive(clamped);
    if (trackRef.current) {
      trackRef.current.scrollTo({ left: clamped * cardStep(), behavior: 'smooth' });
    }
  };

  const stopAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = null;
  };

  useEffect(() => {
    autoplayRef.current = setInterval(() => {
      setActive((prev) => {
        const next = (prev + 1) % CASES.length;
        if (trackRef.current) {
          trackRef.current.scrollTo({ left: next * cardStep(), behavior: 'smooth' });
        }
        return next;
      });
    }, AUTOPLAY_MS);
    return stopAutoplay;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const handleTrackScroll = () => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      const track = trackRef.current;
      if (!track) return;
      const step = cardStep();
      if (!step) return;
      const idx = Math.round(track.scrollLeft / step);
      setActive(Math.max(0, Math.min(CASES.length - 1, idx)));
    }, 100);
  };

  return (
    <section className="ivori-landing-board-section ivori-landing-fade-up" id="board" ref={sectionRef}>
      <div className="ivori-landing-grain" />
      <div className="ivori-landing-board-inner">
        <div className="ivori-landing-section-eyebrow">The Decision Board</div>
        <h2 className="ivori-landing-demo-title">A board of decisions, considered one at a time.</h2>
      </div>

      <div className="ivori-landing-board-slider-wrap">
        <button
          className="ivori-landing-board-nav"
          aria-label="Previous decision"
          onClick={() => {
            stopAutoplay();
            scrollToCard(active - 1);
          }}
        >
          ‹
        </button>
        <div
          className="ivori-landing-board-track"
          ref={trackRef}
          onPointerDown={stopAutoplay}
          onWheel={stopAutoplay}
          onScroll={handleTrackScroll}
        >
          {CASES.map((c, i) => (
            <div
              className="ivori-landing-board-card"
              key={c.who}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
            >
              <div className="ivori-landing-board-card-index">
                {String(i + 1).padStart(2, '0')} / {String(CASES.length).padStart(2, '0')}
              </div>
              <div className="ivori-landing-board-card-who">{c.who}</div>
              <div className="ivori-landing-board-card-decision">{c.decision}</div>
            </div>
          ))}
        </div>
        <button
          className="ivori-landing-board-nav"
          aria-label="Next decision"
          onClick={() => {
            stopAutoplay();
            scrollToCard(active + 1);
          }}
        >
          ›
        </button>
      </div>

      <div className="ivori-landing-board-dots">
        {CASES.map((c, i) => (
          <button
            type="button"
            key={c.who}
            className={`ivori-landing-board-dot${i === active ? ' ivori-landing-active' : ''}`}
            aria-label={`Go to decision ${i + 1}`}
            onClick={() => {
              stopAutoplay();
              scrollToCard(i);
            }}
          />
        ))}
      </div>
    </section>
  );
}
